import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { TrialProvider, useTrialContext } from "@/contexts/TrialContext";
import { useSubscriptionStatus } from "@/hooks/use-subscription-status";
import { useUserProgress } from "@/hooks/use-user-progress";
import { useWelcomeEmails } from "@/hooks/use-welcome-emails";

const REASONS = [
  "Strategic planning and guidance",
  "Document generation",
  "Cost-effective alternative to consultants",
  "AI-powered business insights",
  "Other"
];

const WelcomeContent = () => {
  const [selectedReason, setSelectedReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showTrialSuccess, setShowTrialSuccess] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { hasActiveTrial, isLoading: trialLoading } = useTrialContext();
  const { hasActiveSubscription, isLoading: subscriptionLoading } = useSubscriptionStatus();
  const { progress, isLoading: progressLoading } = useUserProgress();
  const { scheduleWelcomeEmails } = useWelcomeEmails();

  // Check authentication and existing data
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          navigate('/auth');
          return;
        }

        // Wait for subscription, trial, and progress status to load
        if (subscriptionLoading || trialLoading || progressLoading) {
          return;
        }

        // If user has active subscription, redirect to dashboard
        if (hasActiveSubscription) {
          navigate('/dashboard');
          return;
        }

        // Check if user already has onboarding response
        const { data: onboardingData } = await supabase
          .from("onboarding_responses")
          .select("*")
          .eq("user_id", session.user.id)
          .maybeSingle();

        if (onboardingData) {
          // User has already completed onboarding
          if (hasActiveTrial) {
            // Has active trial, check if assessment is completed
            if (progress.hasCompletedAssessment) {
              // Assessment completed, go to dashboard
              console.log("User has completed onboarding and assessment, redirecting to dashboard");
              navigate('/dashboard');
            } else {
              // Show assessment continuation screen
              console.log("User has completed onboarding but needs to complete assessment");
              setShowTrialSuccess(true);
              setIsLoading(false);
            }
          } else {
            // No active trial or subscription, go to plan selection
            console.log("User has completed onboarding but no active access, redirecting to plan selection");
            navigate('/plan-selection');
          }
          return;
        }

        // User needs to complete onboarding
        console.log("User needs to complete onboarding");
        setIsLoading(false);
      } catch (error) {
        console.error("Error checking auth:", error);
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [navigate, hasActiveTrial, hasActiveSubscription, subscriptionLoading, trialLoading, progressLoading, progress.hasCompletedAssessment]);

  const handleSubmit = async () => {
    if (!selectedReason) {
      toast({
        title: "Please select a reason",
        description: "Let us know why you're interested in CSO Agent",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("Not authenticated");
      }

      console.log("Saving onboarding response for user:", user.id);

      const { error } = await supabase
        .from("onboarding_responses")
        .insert({
          reason: selectedReason,
          user_id: user.id
        });

      if (error) {
        console.error("Supabase error:", error);
        throw error;
      }

      console.log("Onboarding response saved successfully");

      // Schedule welcome emails for the user
      try {
        await scheduleWelcomeEmails.mutateAsync({
          userId: user.id,
          userEmail: user.email || ''
        });
        console.log("Welcome emails scheduled successfully");
      } catch (emailError) {
        console.error("Error scheduling welcome emails:", emailError);
        // Don't fail the onboarding if email scheduling fails
      }

      // After successful onboarding, check user access
      if (hasActiveTrial) {
        // Free trial users see success message and can proceed
        console.log("Setting trial success state for user");
        setShowTrialSuccess(true);
      } else if (hasActiveSubscription) {
        // Subscribed users go to dashboard
        console.log("Redirecting subscribed user to dashboard");
        navigate("/dashboard");
      } else {
        // No access, go to plan selection
        console.log("Redirecting to plan selection");
        navigate("/plan-selection");
      }
    } catch (error) {
      console.error("Error saving response:", error);
      toast({
        title: "Error",
        description: "Failed to save your response. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProceedToAssessment = () => {
    console.log("Proceeding to assessment page");
    navigate("/assessment");
  };

  // Show loading state while checking auth/data
  if (isLoading || subscriptionLoading || trialLoading || progressLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cso-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show trial success screen for users who have completed onboarding and have active trial
  if (showTrialSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
        <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-md text-center">
          <div>
            <img 
              src="/lovable-uploads/b17da96c-24f7-4f7f-97cc-f6e8b3220bbf.png" 
              alt="CSO Agent Logo" 
              className="mx-auto h-12 w-12"
            />
            <h1 className="mt-6 text-3xl font-bold text-gray-900">
              Welcome Back!
            </h1>
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-700 font-medium">
                🎉 Your 24-hour free trial is active!
              </p>
              <p className="text-sm text-green-600 mt-2">
                Complete your business assessment to unlock personalized insights and document templates.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <Button
              className="w-full bg-cso-600 hover:bg-cso-700 text-lg py-3"
              onClick={handleProceedToAssessment}
            >
              Complete Business Assessment
            </Button>
            <p className="text-sm text-gray-500">
              Takes 5 minutes • Unlocks personalized features
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show regular onboarding form
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-md">
        <div className="text-center">
          <img 
            src="/lovable-uploads/b17da96c-24f7-4f7f-97cc-f6e8b3220bbf.png" 
            alt="CSO Agent Logo" 
            className="mx-auto h-12 w-12"
          />
          <h1 className="mt-6 text-3xl font-bold text-gray-900">
            Welcome to CSO Agent
          </h1>
          <p className="mt-2 text-gray-600">
            Help us understand how we can best assist you
          </p>
          {hasActiveTrial && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-700 font-medium">
                🎉 Your 24-hour free trial is now active!
              </p>
            </div>
          )}
        </div>

        <div className="mt-8 space-y-4">
          <p className="text-lg font-medium text-gray-900">
            What brings you to CSO Agent today?
          </p>
          <div className="space-y-3">
            {REASONS.map((reason) => (
              <div
                key={reason}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                  selectedReason === reason
                    ? "border-cso-600 bg-cso-50"
                    : "border-gray-200 hover:border-cso-300"
                }`}
                onClick={() => setSelectedReason(reason)}
              >
                <p className="text-gray-900">{reason}</p>
              </div>
            ))}
          </div>
        </div>

        <Button
          className="w-full bg-cso-600 hover:bg-cso-700"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Saving..." : "Continue"}
        </Button>
      </div>
    </div>
  );
};

const Welcome = () => {
  return (
    <TrialProvider>
      <WelcomeContent />
    </TrialProvider>
  );
};

export default Welcome;
